class PremiumReelCarousel extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-reveal-ready');
    this.track = this.querySelector('[data-prc-track]');
    this.slides = Array.from(this.querySelectorAll('[data-prc-slide]'));
    this.currentOutput = this.querySelector('[data-prc-current]');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopQuery = window.matchMedia('(min-width: 990px)');
    this.index = 0;
    this.soundIndex = null;
    this.soundCard = null;
    this.isWrapping = false;
    this.interval = Number(this.dataset.autoplayInterval) || 3000;
    this.videoAutoplay = this.dataset.videoAutoplay !== 'false';

    this.slides.forEach((slide, index) => {
      slide.dataset.prcIndex = String(index);
      const video = slide.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
    });

    this.setupLoopClones();
    this.videoCards = Array.from(this.querySelectorAll('.prc__card'));
    this.addEventListener('click', this.onClick);
    this.addEventListener('play', this.onMediaStateChange, true);
    this.addEventListener('pause', this.onMediaStateChange, true);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.track?.addEventListener('pointerdown', this.onPointerDown);
    this.track?.addEventListener('pointerup', this.onPointerUp);
    this.track?.addEventListener('pointercancel', this.onPointerUp);
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });
    this.reducedMotionQuery.addEventListener('change', this.onMotionChange);
    this.desktopQuery.addEventListener('change', this.onViewportChange);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.addEventListener('pointerleave', this.onSectionLeave);
    this.addEventListener('focusout', this.onSectionFocusOut);
    this.setupVideoObserver();
    this.setupObserver();
    this.updateUi();

    requestAnimationFrame(() => {
      this.alignElement(this.slides[0], 'auto');
      this.activate(0);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('play', this.onMediaStateChange, true);
    this.removeEventListener('pause', this.onMediaStateChange, true);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    this.track?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.reducedMotionQuery?.removeEventListener('change', this.onMotionChange);
    this.desktopQuery?.removeEventListener('change', this.onViewportChange);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.removeEventListener('pointerleave', this.onSectionLeave);
    this.removeEventListener('focusout', this.onSectionFocusOut);
    this.observer?.disconnect();
    this.videoObserver?.disconnect();
    this.stopAutoplay();
    clearTimeout(this.restartTimer);
    clearTimeout(this.scrollTimer);
    clearTimeout(this.wrapTimer);
    clearTimeout(this.resumePlaybackTimer);
    this.loopClones?.forEach((clone) => clone.remove());
  }

  setupLoopClones() {
    if (!this.track || this.slides.length < 2) return;
    const cloneCount = 5;
    const beforeFragment = document.createDocumentFragment();
    const afterFragment = document.createDocumentFragment();
    this.beforeClones = [];
    this.afterClones = [];

    for (let offset = cloneCount; offset >= 1; offset -= 1) {
      const index = (this.slides.length - (offset % this.slides.length)) % this.slides.length;
      const clone = this.prepareClone(this.slides[index], 'before', index);
      this.beforeClones.push(clone);
      beforeFragment.append(clone);
    }

    for (let offset = 0; offset < cloneCount; offset += 1) {
      const index = offset % this.slides.length;
      const clone = this.prepareClone(this.slides[index], 'after', index);
      this.afterClones.push(clone);
      afterFragment.append(clone);
    }

    this.track.prepend(beforeFragment);
    this.track.append(afterFragment);
    this.lastClone = this.beforeClones[this.beforeClones.length - 1];
    this.firstClone = this.afterClones[0];
    this.loopClones = [...this.beforeClones, ...this.afterClones];
  }

  prepareClone(source, edge, logicalIndex) {
    const clone = source.cloneNode(true);
    clone.classList.add('is-clone');
    clone.classList.remove('is-active', 'is-playing');
    clone.removeAttribute('data-prc-slide');
    clone.dataset.prcClone = edge;
    clone.dataset.prcLogicalIndex = String(logicalIndex);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    clone.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.muted = true;
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.preload = 'none';
    });
    return clone;
  }

  onClick = (event) => {
    const soundButton = event.target.closest('[data-prc-sound]');
    if (soundButton && this.contains(soundButton)) {
      this.toggleSound(soundButton.closest('.prc__card'));
      return;
    }

    const mediaButton = event.target.closest('[data-prc-media-toggle]');
    if (mediaButton && this.contains(mediaButton)) {
      this.toggleMedia(mediaButton.closest('[data-prc-slide]'));
      return;
    }

    if (event.target.closest('[data-prc-previous]')) {
      this.releaseSound(false);
      this.previous();
      this.restartAutoplay();
      return;
    }

    if (event.target.closest('[data-prc-next]')) {
      this.releaseSound(false);
      this.advance();
      this.restartAutoplay();
    }
  };

  onPointerDown = () => {
    if (this.soundIndex !== null) return;
    clearTimeout(this.restartTimer);
    this.stopAutoplay();
  };

  onPointerUp = () => {
    if (this.soundIndex !== null) {
      this.centerElement(this.soundCard || this.slides[this.soundIndex]);
      return;
    }
    this.updateIndexFromScroll();
    this.restartAutoplay();
  };

  onScroll = () => {
    if (this.isWrapping || this.soundIndex !== null) return;
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => this.updateIndexFromScroll(), 110);
  };

  onMotionChange = () => {
    this.onViewportChange();
  };

  onViewportChange = () => {
    if (this.soundIndex !== null) this.centerElement(this.soundCard || this.slides[this.soundIndex], 'auto');
    else this.alignElement(this.slides[this.index], 'auto');
    this.scheduleVisiblePlayback();
    this.startAutoplay();
  };

  onVisibilityChange = () => {
    if (document.hidden) {
      this.releaseSound(false);
      this.stopAutoplay();
      this.pauseAll();
    } else {
      this.activate(this.index);
      this.playVisibleVideos();
      this.startAutoplay();
    }
  };

  onSectionLeave = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.exitSoundFocus();
  };

  onSectionFocusOut = () => {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.exitSoundFocus();
    }, 0);
  };

  onBlockSelect = (event) => {
    const slide = event.target.closest?.('[data-prc-slide]');
    if (!slide) return;
    this.releaseSound(false);
    this.goTo(Number(slide.dataset.prcIndex));
    this.restartAutoplay();
  };

  onMediaStateChange = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const slide = video.closest('.prc__card');
    if (!slide) return;
    const playing = event.type === 'play';
    slide.classList.toggle('is-playing', playing);
    const toggle = slide.querySelector('[data-prc-media-toggle]');
    if (toggle) toggle.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${this.cardName(slide)}`);
  };

  startAutoplay = () => {
    this.stopAutoplay();
    if (
      this.dataset.autoplay !== 'true' ||
      this.soundIndex !== null ||
      document.hidden ||
      this.reducedMotionQuery.matches ||
      this.slides.length < 2
    ) return;
    this.timer = setInterval(() => this.advance(), this.interval);
  };

  stopAutoplay = () => {
    clearInterval(this.timer);
    this.timer = undefined;
  };

  restartAutoplay() {
    this.stopAutoplay();
    clearTimeout(this.restartTimer);
    this.restartTimer = setTimeout(() => {
      this.startAutoplay();
      if (this.timer) this.advance();
    }, this.interval);
  }

  advance() {
    if (this.index < this.slides.length - 1) {
      this.goTo(this.index + 1);
      return;
    }
    this.wrapTo(this.firstClone, 0);
  }

  previous() {
    if (this.index > 0) {
      this.goTo(this.index - 1);
      return;
    }
    this.wrapTo(this.lastClone, this.slides.length - 1);
  }

  goTo(index, behavior = 'smooth') {
    const slide = this.slides[index];
    if (!slide) return;
    this.index = index;
    this.alignElement(slide, behavior);
    this.activate(index);
  }

  wrapTo(clone, targetIndex) {
    if (!clone || this.isWrapping) {
      this.goTo(targetIndex, 'auto');
      return;
    }
    this.isWrapping = true;
    this.stopAutoplay();
    this.pauseAll();
    this.slides.forEach((slide) => slide.classList.remove('is-active'));
    clone.classList.add('is-active');
    this.alignElement(clone, this.reducedMotionQuery.matches ? 'auto' : 'smooth');
    this.scheduleVisiblePlayback();
    clearTimeout(this.wrapTimer);
    this.wrapTimer = setTimeout(() => {
      this.index = targetIndex;
      clone.classList.remove('is-active');
      this.alignElement(this.slides[targetIndex], 'auto');
      this.activate(targetIndex);
      this.isWrapping = false;
      this.scheduleVisiblePlayback();
      this.startAutoplay();
    }, this.reducedMotionQuery.matches ? 20 : 520);
  }

  centerElement(element, behavior = 'smooth') {
    if (!this.track || !element) return;
    const left = element.offsetLeft - (this.track.clientWidth - element.offsetWidth) / 2;
    this.track.scrollTo({ left, behavior });
  }

  alignElement(element, behavior = 'smooth') {
    if (!this.track || !element) return;
    if (!this.desktopQuery.matches) {
      this.centerElement(element, behavior);
      return;
    }
    this.track.scrollTo({ left: element.offsetLeft, behavior });
  }

  updateIndexFromScroll() {
    if (!this.track || this.slides.length === 0 || this.isWrapping) return;
    const desktop = this.desktopQuery.matches;
    const viewportAnchor = desktop ? this.track.scrollLeft : this.track.scrollLeft + this.track.clientWidth / 2;
    const candidates = [...(this.beforeClones || []), ...this.slides, ...(this.afterClones || [])];
    let closest = candidates[0];
    let distance = Number.POSITIVE_INFINITY;

    candidates.forEach((slide) => {
      const slideAnchor = desktop ? slide.offsetLeft : slide.offsetLeft + slide.offsetWidth / 2;
      const nextDistance = Math.abs(slideAnchor - viewportAnchor);
      if (nextDistance >= distance) return;
      distance = nextDistance;
      closest = slide;
    });

    if (closest.classList.contains('is-clone')) {
      this.goTo(Number(closest.dataset.prcLogicalIndex), 'auto');
      return;
    }

    const index = this.slides.indexOf(closest);
    if (index >= 0 && index !== this.index) this.activate(index);
    this.index = Math.max(0, index);
    this.updateUi();
  }

  activate(index) {
    this.index = index;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-current', active ? 'true' : 'false');
      const video = slide.querySelector('video');
      if (!video) return;

      const isSoundTarget = this.soundCard === slide;
      video.muted = !isSoundTarget;
      video.defaultMuted = !isSoundTarget;

      if (this.soundIndex !== null) {
        if (isSoundTarget && this.isInView !== false) {
          video.preload = 'metadata';
          video.play().catch(() => {});
        } else {
          this.stopVideo(video);
        }
        return;
      }

      if (this.videoAutoplay) {
        const isVisible = slide.dataset.prcVideoVisible === 'true';
        if (this.isInView !== false && (isVisible || active)) {
          this.resumeMutedVideo(video);
        } else if (!isVisible) {
          this.stopVideo(video);
        }
      } else if (!active) {
        this.stopVideo(video);
      }
    });
    this.updateSoundButtons();
    this.updateUi();
  }

  toggleMedia(slide) {
    if (!slide) return;
    const index = Number(slide.dataset.prcIndex);
    const video = slide.querySelector('video');
    if (!video) return;
    if (index !== this.index) {
      this.releaseSound(false);
      this.goTo(index);
      this.restartAutoplay();
      return;
    }
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }

  toggleSound(card) {
    if (!card) return;
    const isClone = card.classList.contains('is-clone');
    const index = Number(isClone ? card.dataset.prcLogicalIndex : card.dataset.prcIndex);
    const video = card.querySelector('video');
    if (!video) return;
    const enableSound = this.soundCard !== card || video.muted;

    if (!enableSound) {
      this.exitSoundFocus();
      return;
    }

    this.stopAutoplay();
    clearTimeout(this.restartTimer);
    this.pauseAll();
    this.soundIndex = index;
    this.soundCard = card;
    this.classList.add('is-sound-active');
    this.index = index;
    this.centerElement(card);
    this.videoCards.forEach((videoCard) => videoCard.classList.toggle('is-active', videoCard === card));
    this.slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-current', slideIndex === index ? 'true' : 'false');
    });
    this.updateUi();

    video.muted = false;
    video.defaultMuted = false;
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.volume = 1;
    video.preload = 'metadata';
    video.play().catch(() => {
      this.exitSoundFocus();
    });
    this.updateSoundButtons();
  }

  releaseSound(restart = true) {
    const hadSound = this.soundCard !== null;
    this.soundIndex = null;
    this.soundCard = null;
    this.classList.remove('is-sound-active');
    this.videoCards.forEach((card) => card.classList.remove('is-active'));
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-current', active ? 'true' : 'false');
    });
    this.videoCards.forEach((card) => {
      const video = card.querySelector('video');
      if (video) {
        video.muted = true;
        video.defaultMuted = true;
      }
    });
    this.updateSoundButtons();
    this.playVisibleVideos();
    if (hadSound && restart) this.alignElement(this.slides[this.index]);
    if (restart) this.restartAutoplay();
  }

  playVisibleVideos() {
    if (!this.videoAutoplay || this.isInView === false || document.hidden) return;

    this.videoCards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;

      if (this.soundIndex !== null) {
        if (card === this.soundCard) video.play().catch(() => {});
        else this.stopVideo(video);
        return;
      }

      const isVisible = this.isSlideVisible(card);
      const isActiveFallback = !this.videoObserver && card === this.slides[this.index];
      if (isVisible || isActiveFallback) {
        this.resumeMutedVideo(video);
      } else {
        this.stopVideo(video);
      }
    });
  }

  resumeMutedVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.setAttribute('autoplay', '');
    video.preload = 'metadata';
    video.play().catch(() => {});
  }

  stopVideo(video) {
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.pause();
  }

  isSlideVisible(slide) {
    if (!this.track || !slide) return false;
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    return slideRect.right > trackRect.left + 2 && slideRect.left < trackRect.right - 2;
  }

  scheduleVisiblePlayback() {
    clearTimeout(this.resumePlaybackTimer);
    requestAnimationFrame(() => this.playVisibleVideos());
    this.resumePlaybackTimer = setTimeout(
      () => this.playVisibleVideos(),
      this.reducedMotionQuery.matches ? 40 : 560
    );
  }

  exitSoundFocus() {
    if (this.soundIndex === null) return;
    const focusedClone = this.soundCard?.classList.contains('is-clone');
    this.releaseSound(false);
    const activeSlide = this.slides[this.index];
    if (activeSlide) this.alignElement(activeSlide, focusedClone ? 'auto' : 'smooth');
    this.scheduleVisiblePlayback();
    this.startAutoplay();
  }

  updateSoundButtons() {
    this.videoCards.forEach((card) => {
      const button = card.querySelector('[data-prc-sound]');
      if (!button) return;
      const active = this.soundCard === card;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', `${active ? 'Turn sound off for' : 'Turn sound on for'} ${this.cardName(card)}`);
    });
  }

  updateUi() {
    if (this.currentOutput) this.currentOutput.textContent = String(this.index + 1).padStart(2, '0');
    const progress = this.slides.length > 1 ? this.index / (this.slides.length - 1) : 1;
    this.style.setProperty('--prc-progress', String(progress));
  }

  pauseAll() {
    this.videoCards.forEach((card) => {
      const video = card.querySelector('video');
      if (video) this.stopVideo(video);
    });
  }

  cardName(slide) {
    return slide.querySelector('.prc__caption h3')?.textContent?.trim() || 'reel';
  }

  setupObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      this.hasBeenInView = true;
      this.classList.add('is-revealed');
      this.activate(this.index);
      this.playVisibleVideos();
      this.startAutoplay();
      return;
    }

    this.isInView = false;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isInView = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.hasBeenInView = true;
          this.classList.add('is-revealed');
          this.activate(this.index);
          this.playVisibleVideos();
          this.startAutoplay();
        } else {
          this.releaseSound(false);
          this.pauseAll();
          if (this.hasBeenInView) this.startAutoplay();
          else this.stopAutoplay();
        }
      },
      { rootMargin: '160px 0px', threshold: 0.04 }
    );
    this.observer.observe(this);
  }

  setupVideoObserver() {
    if (!('IntersectionObserver' in window)) {
      this.videoCards.forEach((card) => {
        card.dataset.prcVideoVisible = this.isSlideVisible(card) ? 'true' : 'false';
      });
      return;
    }

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const video = card.querySelector('video');
          if (!video) return;

          card.dataset.prcVideoVisible = entry.isIntersecting ? 'true' : 'false';

          if (this.soundIndex !== null) {
            if (card === this.soundCard && entry.isIntersecting && this.isInView !== false) {
              video.preload = 'metadata';
              video.play().catch(() => {});
            } else {
              this.stopVideo(video);
            }
            return;
          }

          if (this.videoAutoplay && entry.isIntersecting && this.isInView !== false) {
            this.resumeMutedVideo(video);
          } else if (!entry.isIntersecting) {
            this.stopVideo(video);
          }
        });
      },
      { threshold: 0.18, rootMargin: '80px 0px' }
    );

    this.videoCards.forEach((card) => this.videoObserver.observe(card));
  }
}

if (!customElements.get('premium-reel-carousel')) {
  customElements.define('premium-reel-carousel', PremiumReelCarousel);
}
